import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { onAuthStateChanged, type User } from "firebase/auth"
import { io, type Socket } from "socket.io-client"

import { auth } from "@/features/auth/providers/firebase"
import { useSubscriptionStore } from "@/stores/subscription-store"
import { useLocalStorage } from "@/shared/hooks/use-local-storage"
import { getParamsFromStorage, setParamsToStorage } from "@/shared/lib/storage-params"
import { env } from "@/config/env"
import type { Advisor } from "@/features/advisors"

import { ChatContext, type ChatContextValue } from "./chat-context"
import { buildSocketAuthToken, getChatUserId } from "../lib/socket-auth"
import { freeMinutesFromUsedCredits } from "../lib/start-credits"
import { liveChatPlans } from "../config/plans"
import {
  ASSISTANT_STATUSES,
  MESSAGE_ROLE,
  type AssistantMessageResponse,
  type AssistantStatusResponse,
  type ChatBalanceResponse,
  type ChatMessages,
  type ChatsMessage,
  type ChatStartedResponse,
  type ChatStoppedResponse,
  type MessageRole,
  type PalmReadingData,
  type Thread,
  type UnreadAdvisor,
} from "../types"

interface HistoryEvent {
  // The thread this history is for — used to ignore responses for other threads
  // (the same event populates both the open conversation and list previews).
  threadId?: string
  data: Array<{
    role: MessageRole
    content: Array<{ text?: { value?: string } }>
    assistant_id?: string
  }>
}

function getRandomNumberInInterval(max: number, min: number) {
  return Math.floor(Math.random() * (max - min + 1)) + Math.floor(min)
}

function nowUnixSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [unreadAdvisorIds, setUnreadAdvisorIds] = useLocalStorage<string[]>("chat-unread", [])
  const [unreadAdvisors, setUnreadAdvisors] = useLocalStorage<UnreadAdvisor[]>("advisor_unread", [])
  const [palmReadingData, setPalmReadingData] = useLocalStorage<PalmReadingData>("palm_data", null)
  const [isPalmReadingLocked, setIsPalmReadingLocked] = useState(false)
  const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null)
  const userMainSubscriptionId = useSubscriptionStore((s) => s.subscription?.id ?? null)
  const [messages, setMessages] = useState<ChatMessages>([])
  const [chatsMessages, setChatsMessages] = useState<ChatsMessage[]>([])
  const [threads, setThreads] = useState<Thread[]>([])
  const [activityAtByAssistant, setActivityAtByAssistant] = useState<Record<string, number>>({})
  const [preSelectedChatMessage, setPreSelectedChatMessage] = useState<string | null>(null)
  const [userCreditsCount, setUserCreditsCount] = useState<number | null>(null)
  const [userUsedCredits, setUserUsedCredits] = useState<number | null>(null)
  const [freeMinutesLeft, setFreeMinutesLeft] = useState<number | null>(null)
  const [assistantStatuses, setAssistantStatuses] = useState<AssistantStatusResponse[]>([])
  const [palmReportModalOpen, setPalmReportModalOpen] = useState(false)
  const [chatWithAdvisorModalOpen, setChatWithAdvisorModalOpen] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const [user, setUser] = useState<User | null>(auth.currentUser)
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null)
  // Mirror of currentThreadId for use inside the socket handlers, which are
  // registered once (on connect) and would otherwise close over a stale value.
  const currentThreadIdRef = useRef<string | null>(null)
  // Thread id just created by a live reply (not by opening an existing chat).
  // Used to skip the one history reload that would otherwise race with the
  // in-progress paced delivery and duplicate the later bubbles.
  const skipHistoryForThreadRef = useRef<string | null>(null)

  const { credits_purchased } = getParamsFromStorage(["credits_purchased"])
  const [hasPurchasedCredits, setHasPurchasedCredits] = useState(
    credits_purchased === "true",
  )

  const markCreditsPurchased = useCallback(() => {
    setParamsToStorage({ credits_purchased: "true" })
    setHasPurchasedCredits(true)
  }, [])

  // Track the firebase user reactively (it resolves asynchronously after mount).
  useEffect(() => {
    return onAuthStateChanged(auth, setUser)
  }, [])

  // Keep the ref in sync so socket handlers see the current open thread.
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId
  }, [currentThreadId])

  // A reply paced over several bubbles is aborted server-side if the socket
  // drops mid-delivery (mobile backgrounding). The full reply is already stored
  // in the OpenAI conversation, so on reconnect / tab-visible we re-pull the
  // open thread's history to recover any dropped bubbles. Emitting `history`
  // directly bypasses the post-create skip guard (which is only for the initial
  // live reply), and never consumes it.
  const resyncOpenThread = useCallback(() => {
    const threadId = currentThreadIdRef.current
    if (!threadId || !socketRef.current || !user) return
    socketRef.current.emit("history", { userId: getChatUserId(user), threadId })
  }, [user])

  // Clear a typing indicator left stuck on "processing" because the terminal
  // `completed` status never arrived (delivery aborted on disconnect).
  const resetStaleStatuses = useCallback(() => {
    setAssistantStatuses((prev) =>
      prev.map((s) =>
        s.status === ASSISTANT_STATUSES.PROCESSING
          ? { ...s, status: ASSISTANT_STATUSES.COMPLETED }
          : s,
      ),
    )
  }, [])

  useEffect(() => {
    if (!user) return

    const userId = getChatUserId(user)
    let socket: Socket | null = null
    let cancelled = false

    // Token signing is async (Web Crypto); guard against unmount mid-build.
    buildSocketAuthToken(user).then((authToken) => {
      if (cancelled) return

      socket = io(env.VITE_ADVISORS_SOCKET_URL, {
        transports: ["websocket"],
        auth: { token: authToken },
      })
      socketRef.current = socket

      const requestBalance = () => {
        socket?.emit("used_credits")
      }

      socket.on("connect", () => {
        socket?.emit("my_threads", { userId })
        // Balance drives free-minutes badges — fetch immediately, don't wait
        // for the 1s poll (and cover the case where `connect` already fired
        // before this listener was attached — see `socket.connected` below).
        requestBalance()
        // Recover from a drop mid-delivery: clear stuck typing and re-pull the
        // open thread so any bubbles the server couldn't send are restored.
        resetStaleStatuses()
        resyncOpenThread()
      })

      socket.on("used_credits", (resp: ChatBalanceResponse) => {
        const used = Number(resp.used_credits) || 0
        setUserCreditsCount(resp.remaining_credits)
        setUserUsedCredits(used)
        setFreeMinutesLeft(freeMinutesFromUsedCredits(used))
      })

      // io() may connect before `connect` listeners are registered.
      if (socket.connected) {
        socket.emit("my_threads", { userId })
        requestBalance()
      }

      socket.on("assistant_status", (resp: AssistantStatusResponse) => {
        setAssistantStatuses((prev) => {
          const existingIndex = prev.findIndex((status) => status.assistantId === resp.assistantId)
          if (existingIndex !== -1) {
            const updated = [...prev]
            updated[existingIndex] = resp
            return updated
          }
          return [...prev, resp]
        })
      })

      socket.on("history", (history: HistoryEvent) => {
        // Replace the open conversation ONLY with the response for that thread.
        // The same event also delivers limit:1 previews for every other thread;
        // applying those here is what made another advisor's message appear in
        // the open chat. (Fallback to apply when threadId is absent keeps an
        // older backend working.) The server splits multi-bubble replies into
        // separate items, so map 1:1; history.data is newest-first → reverse.
        if (!history.threadId || history.threadId === currentThreadIdRef.current) {
          const result = history.data.map((el) => ({
            role: el.role,
            content: el?.content[0]?.text?.value ?? "",
            assistantId: el?.assistant_id,
          }))
          setMessages(result.reverse())
        }

        if (history.data.length === 1) {
          setChatsMessages((prev) => {
            const assistantId = history.data[0].assistant_id ?? ""
            const message = history.data[0].content[0]?.text?.value ?? null
            const exists = prev.find((el) => el.assistantId === assistantId)
            if (exists) {
              return prev.map((el) =>
                el.assistantId === assistantId ? { assistantId, message } : el,
              )
            }
            return [...prev, { assistantId, message }]
          })
        }
      })

      socket.on("my_threads", (resp: Thread[]) => {
        if (resp.length > 0) {
          setThreads(resp)
        } else {
          socket?.emit("chat", { userId })
        }
      })

      socket.on("chat_started", (resp: { assistants: ChatStartedResponse[] }) => {
        setChatsMessages((prev) => [
          ...prev,
          ...resp.assistants.map((el) => ({ assistantId: el.assistantId, message: null })),
        ])
      })

      socket.on("chat_stopped", (resp: ChatStoppedResponse[]) => {
        resp.forEach((newValue) => {
          const timeout = getRandomNumberInInterval(8, 14) * 1000
          setTimeout(() => {
            setChatsMessages((prev) =>
              prev.map((prevValue) =>
                prevValue.assistantId === newValue.assistantId
                  ? { ...prevValue, message: newValue.assistantReply.text.value }
                  : prevValue,
              ),
            )

            setUnreadAdvisorIds((prev) => [...prev, newValue.assistantId])
            setUnreadAdvisors((prev) => {
              const exists = prev.find((el) => el.id === newValue.assistantId)
              if (exists) {
                return prev.map((el) =>
                  el.id === newValue.assistantId
                    ? { id: newValue.assistantId, message: newValue.assistantReply.text.value }
                    : el,
                )
              }
              return [
                ...prev,
                { id: newValue.assistantId, message: newValue.assistantReply.text.value },
              ]
            })

            socketRef.current?.emit("my_threads", { userId })
            setActivityAtByAssistant((prev) => ({
              ...prev,
              [newValue.assistantId]: nowUnixSeconds(),
            }))
          }, timeout)
        })
      })

      socket.on("connect_error", (err) => {
        console.error("❌ Socket connection error:", err)
      })

      socket.on("disconnect", (reason) => {
        console.log("🔌 Disconnected from advisors socket", reason)
        // Don't leave the typing dots spinning while we're offline; the
        // reconnect resync above does the authoritative recovery.
        resetStaleStatuses()
      })
    })

    return () => {
      cancelled = true
      socket?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Returning to a backgrounded tab (mobile app-switch) is the common repro for
  // a half-delivered reply. If the socket is still up, recover immediately; if
  // it dropped, the `connect` resync handles it instead.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return
      if (!socketRef.current?.connected) return
      resetStaleStatuses()
      resyncOpenThread()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => document.removeEventListener("visibilitychange", onVisible)
  }, [resetStaleStatuses, resyncOpenThread])

  const updateThreads = () => {
    if (socketRef.current && user) {
      socketRef.current.emit("my_threads", { userId: getChatUserId(user) })
    }
  }

  useEffect(() => {
    const handleAssistantMessage = (message: AssistantMessageResponse) => {
      if (selectedAdvisor && message.assistantId === selectedAdvisor.assistantId) {
        if (!currentThreadId) {
          // First reply of a new chat: bubbles are already rendered live, so flag
          // this thread to skip the history reload that setting it would trigger.
          skipHistoryForThreadRef.current = message.threadId
          setCurrentThreadId(message.threadId)
        }
        setMessages((prev) => [
          ...prev,
          { role: MESSAGE_ROLE.ASSISTANT, content: message.assistantReply.text.value },
        ])
        setActivityAtByAssistant((prev) => ({
          ...prev,
          [message.assistantId]: nowUnixSeconds(),
        }))
      } else {
        setUnreadAdvisorIds((prev) => [...prev, message.assistantId])
        setUnreadAdvisors((prev) => {
          const exists = prev.find((el) => el.id === message.assistantId)
          if (exists) {
            return prev.map((el) =>
              el.id === message.assistantId
                ? { id: message.assistantId, message: message.assistantReply.text.value }
                : el,
            )
          }
          return [...prev, { id: message.assistantId, message: message.assistantReply.text.value }]
        })

        setChatsMessages((prev) => {
          const assistantId = message.assistantId
          const newMessage = message.assistantReply.text.value
          const exists = prev.find((el) => el.assistantId === assistantId)
          if (exists) {
            return prev.map((el) =>
              el.assistantId === assistantId ? { assistantId, message: newMessage } : el,
            )
          }
          return [...prev, { assistantId, message: newMessage }]
        })
        setActivityAtByAssistant((prev) => ({
          ...prev,
          [message.assistantId]: nowUnixSeconds(),
        }))
      }
    }

    const socket = socketRef.current
    socket?.on("assistant_message", handleAssistantMessage)
    return () => {
      socket?.off("assistant_message", handleAssistantMessage)
    }
  }, [selectedAdvisor, currentThreadId, setUnreadAdvisorIds, setUnreadAdvisors])

  // Poll the credit balance once per second (matches source behavior).
  useEffect(() => {
    const interval = setInterval(() => {
      socketRef.current?.emit("used_credits")
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (currentThreadId && socketRef.current && user) {
      // Skip exactly one reload for a thread we just created live — its bubbles
      // are already on screen and a reload would duplicate the later ones.
      if (skipHistoryForThreadRef.current === currentThreadId) {
        skipHistoryForThreadRef.current = null
        return
      }
      socketRef.current.emit("history", {
        userId: getChatUserId(user),
        threadId: currentThreadId,
      })
    } else {
      setMessages([])
    }
  }, [currentThreadId, user])

  useEffect(() => {
    if (threads.length > 0 && user) {
      const userId = getChatUserId(user)
      threads.forEach((thread) => {
        // Don't fetch a preview for the open thread — its single-message preview
        // response would match the threadId gate and clobber the full view.
        if (thread.threadId === currentThreadIdRef.current) return
        socketRef.current?.emit("history", {
          userId,
          threadId: thread.threadId,
          query: { limit: 1 },
        })
      })
    }
  }, [threads, user])

  // Sync the open thread to the selected advisor; clearing it lets the effect
  // above reset the message list.
  useEffect(() => {
    const threadId = selectedAdvisor
      ? (threads.find((el) => el.assistantId === selectedAdvisor.assistantId)?.threadId ?? null)
      : null
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentThreadId(threadId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdvisor])

  const sendMessage = (message: string) => {
    if (socketRef.current && selectedAdvisor && user) {
      socketRef.current.emit("user_message", {
        userId: getChatUserId(user),
        text: message,
        threadId: currentThreadId,
        assistantId: selectedAdvisor.assistantId,
      })
      setActivityAtByAssistant((prev) => ({
        ...prev,
        [selectedAdvisor.assistantId]: nowUnixSeconds(),
      }))
    }
  }

  const emitEnterChatEvent = () => socketRef.current?.emit("enter_chat")
  const emitLeaveChatEvent = () => socketRef.current?.emit("leave_chat")

  const value: ChatContextValue = {
    unreadAdvisorIds,
    setUnreadAdvisorIds,
    unreadAdvisors,
    setUnreadAdvisors,
    selectedAdvisor,
    setSelectedAdvisor,
    sendMessage,
    chatsMessages,
    messages,
    setMessages,
    assistantStatuses,
    liveChatPlans,
    userMainSubscriptionId,
    updateThreads,
    userCreditsCount,
    emitEnterChatEvent,
    emitLeaveChatEvent,
    preSelectedChatMessage,
    setPreSelectedChatMessage,
    isPalmReadingLocked,
    setIsPalmReadingLocked,
    palmReadingData,
    setPalmReadingData,
    user,
    currentThreadId,
    palmReportModalOpen,
    setPalmReportModalOpen,
    chatWithAdvisorModalOpen,
    setChatWithAdvisorModalOpen,
    threads,
    activityAtByAssistant,
    freeMinutesLeft,
    hasPurchasedCredits,
    markCreditsPurchased,
    userUsedCredits,
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}
